import React, { useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Field, TextInput, Select, Button } from '../ui/primitives';
import { isPastDateTime, nowLocalISO } from '../charter/validation';

const API_BASE = process.env.REACT_APP_MOVORRA_API_URL || 'http://localhost:8081';

// The operational trip statuses a dispatcher can set, in lifecycle order. Mirrors the backend's
// accepted trip_status strings (plain strings, no enum server-side).
const TRIP_STATUSES = ['SCHEDULED', 'CONFIRMED', 'ASSIGNED', 'EN_ROUTE', 'PICKED_UP', 'DROPPED_OFF', 'COMPLETED', 'CANCELLED'];
const STATUS_LABELS = {
  SCHEDULED: 'Scheduled', CONFIRMED: 'Confirmed', ASSIGNED: 'Assigned', EN_ROUTE: 'En route',
  PICKED_UP: 'Picked up', DROPPED_OFF: 'Dropped off', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

// "2026-06-25T17:00:00" -> "2026-06-25T17:00" for <input type="datetime-local">
function toLocalInput(dt) {
  if (!dt) return '';
  return String(dt).slice(0, 16);
}
// "2026-06-25T17:00" -> "2026-06-25T17:00:00" (LocalDateTime the API parses)
function fromLocalInput(v) {
  if (!v) return null;
  return v.length === 16 ? `${v}:00` : v;
}

// Pull the editable shape out of a LocationResponse on the row.
function locFromTrip(loc) {
  loc = loc || {};
  return {
    addressLine1: loc.addressLine1 || '',
    city: loc.city || '',
    state: loc.state || '',
    postalCode: loc.postalCode || '',
    airportCode: loc.airportCode || '',
    airline: loc.airline || '',
    flightNumber: loc.flightNumber || '',
    terminal: loc.terminal || '',
    flightTime: toLocalInput(loc.flightTime),
  };
}

// Turn the form's location back into a LocationRequest. Empty strings for the key
// address fields are dropped (backend keeps the stored value on blank); flight fields
// are sent as-is so they can be cleared.
function locToRequest(form, datetime) {
  const req = {};
  if (datetime !== undefined) req.datetime = datetime;
  if (form.addressLine1.trim()) req.addressLine1 = form.addressLine1.trim();
  if (form.city.trim()) req.city = form.city.trim();
  if (form.state.trim()) req.state = form.state.trim();
  if (form.postalCode.trim()) req.postalCode = form.postalCode.trim();
  if (form.airportCode.trim()) req.airportCode = form.airportCode.trim().toUpperCase();
  req.airline = form.airline.trim();
  req.flightNumber = form.flightNumber.trim();
  req.terminal = form.terminal.trim();
  req.flightTime = fromLocalInput(form.flightTime);
  return req;
}

/**
 * Edit a single booking trip's details (time, pickup/dropoff, flight, travelers) plus its
 * dispatch state (driver, vehicle, status). Trip-field edits persist via
 * PUT /api/bookings/{bookingId}/with-trips (reprices, recalcs totals, syncs the linked Trip,
 * emails the customer on a material change). Driver/vehicle/status persist via the board's
 * applyDispatch, which materialises the standalone Trip once and PATCHes assign-driver /
 * assign-vehicle / status. Scope: this edits ONE trip leg of the booking.
 */
export default function TripEditModal({ row, customer, drivers, vehicles = [], getHeaders, applyDispatch,
                                        testToolsEnabled, onClose, onSaved, toast }) {
  const t = row.trip;
  // Seed the name from the full customer record when we have it, else split the display name.
  const nameParts = (row.customerName || '').trim().split(/\s+/);
  const [firstName, setFirstName] = useState(customer?.firstName ?? (nameParts[0] || ''));
  const [lastName, setLastName] = useState(customer?.lastName ?? (nameParts.slice(1).join(' ') || ''));
  const origFirstName = customer?.firstName ?? (nameParts[0] || '');
  const origLastName = customer?.lastName ?? (nameParts.slice(1).join(' ') || '');
  const [pickupDt, setPickupDt] = useState(toLocalInput(t.pickup?.datetime));
  const [pickup, setPickup] = useState(() => locFromTrip(t.pickup));
  const [dropoff, setDropoff] = useState(() => locFromTrip(t.dropoff));
  const [numTravelers, setNumTravelers] = useState(t.travelers?.numTravelers ?? 1);
  const [numBags, setNumBags] = useState(t.travelers?.numBags ?? 0);
  const [comments, setComments] = useState(t.additionalComments || '');
  const [driverId, setDriverId] = useState(row.driverId ? String(row.driverId) : '');
  const [vehicleId, setVehicleId] = useState(row.linkedTrip?.vehicleId ? String(row.linkedTrip.vehicleId) : '');
  const [status, setStatus] = useState((row.linkedTrip?.tripStatus || t.tripStatus || 'CONFIRMED').toUpperCase());
  const [suppress, setSuppress] = useState(false);
  const [saving, setSaving] = useState(false);

  const origDriverId = row.driverId ? String(row.driverId) : '';
  const origVehicleId = row.linkedTrip?.vehicleId ? String(row.linkedTrip.vehicleId) : '';
  const origStatus = (row.linkedTrip?.tripStatus || t.tripStatus || 'CONFIRMED').toUpperCase();
  const customerLabel = useMemo(() => row.customerName || row.bookingNumber, [row]);

  const setP = (k, v) => setPickup((s) => ({ ...s, [k]: v }));
  const setD = (k, v) => setDropoff((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (saving) return;

    // Detect what actually changed so the toast is honest (and we skip a pointless save).
    // "material" mirrors the backend's rule that fires the customer email + driver re-accept.
    const locKey = (f) => [f.addressLine1, f.city, f.state, f.postalCode, f.airportCode,
      f.airline, f.flightNumber, f.terminal, f.flightTime].map((x) => (x || '').trim().toLowerCase()).join('|');
    const materialChanged =
      pickupDt !== toLocalInput(t.pickup?.datetime)
      || locKey(pickup) !== locKey(locFromTrip(t.pickup))
      || locKey(dropoff) !== locKey(locFromTrip(t.dropoff));
    const otherChanged =
      Number(numTravelers) !== (t.travelers?.numTravelers ?? 1)
      || Number(numBags) !== (t.travelers?.numBags ?? 0)
      || (comments || '') !== (t.additionalComments || '');
    const tripChanged = materialChanged || otherChanged;
    const driverChanged = driverId !== origDriverId;
    const vehicleChanged = vehicleId !== origVehicleId;
    const statusChanged = status !== origStatus;
    const dispatchChanged = driverChanged || vehicleChanged || statusChanged;
    const nameChanged = firstName.trim() !== (origFirstName || '').trim()
      || lastName.trim() !== (origLastName || '').trim();
    const suppressing = suppress && testToolsEnabled;

    if (!tripChanged && !dispatchChanged && !nameChanged) {
      toast?.({ tone: 'info', message: 'No changes made' });
      onClose?.();
      return;
    }

    // Block moving the pickup to a time in the past (only when the pickup time was actually changed —
    // a trip already in the past can still be edited for other fields). The picker `min` is bypassable.
    if (pickupDt && pickupDt !== toLocalInput(t.pickup?.datetime) && isPastDateTime(pickupDt)) {
      toast?.({ tone: 'danger', message: 'Pick a pickup date and time in the future' });
      return;
    }

    setSaving(true);
    try {
      // Customer name — persists per-booking via PUT /api/bookings/{id}/customer. The backend
      // renames the customer in place when it's exclusive to this booking, or forks an independent
      // customer when it's shared, so renaming ONE trip never renames the others.
      if (nameChanged) {
        if (!firstName.trim() || !lastName.trim()) {
          toast?.({ tone: 'danger', message: 'First and last name are required' });
          setSaving(false);
          return;
        }
        const headers = await getHeaders();
        const res = await fetch(`${API_BASE}/api/bookings/${row.bookingId}/customer`, {
          method: 'PUT', headers,
          body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
        });
        if (!res.ok) {
          toast?.({ tone: 'danger', message: `Couldn't save the name (HTTP ${res.status})` });
          setSaving(false);
          return;
        }
      }

      if (tripChanged) {
        const headers = await getHeaders();
        if (suppressing) headers['X-Suppress-Notifications'] = 'true';
        const body = {
          trips: [{
            bookingTripId: t.bookingTripId,
            pickup: locToRequest(pickup, fromLocalInput(pickupDt)),
            dropoff: locToRequest(dropoff, undefined),
            travelers: { numTravelers: Number(numTravelers), numBags: Number(numBags) },
            additionalComments: comments,
          }],
        };
        const res = await fetch(`${API_BASE}/api/bookings/${row.bookingId}/with-trips`, {
          method: 'PUT', headers, body: JSON.stringify(body),
        });
        if (!res.ok) {
          const msg = res.status === 409
            ? "This trip is already underway and can't be edited."
            : `Couldn't save changes (HTTP ${res.status})`;
          toast?.({ tone: 'danger', message: msg });
          setSaving(false);
          return;
        }
      }

      // Driver / vehicle / status — one call so the Trip is materialised only once.
      if (dispatchChanged) {
        const changes = {};
        if (driverChanged) changes.driverId = driverId ? Number(driverId) : null;
        if (vehicleChanged) changes.vehicleId = vehicleId ? Number(vehicleId) : null;
        if (statusChanged) changes.status = status;
        const result = await applyDispatch(row, changes, { suppress: suppressing });
        if (!result?.ok) {
          toast?.({ tone: 'danger', message: result?.error || "Couldn't update dispatch — try again" });
          setSaving(false);
          return;
        }
      }

      // Only a material change actually emails the customer — say so only then (and not if suppressed).
      toast?.({ tone: 'success', message: (materialChanged && !suppressing)
        ? `${row.bookingNumber} updated — customer notified`
        : `${row.bookingNumber} updated` });
      onSaved?.();
    } catch (err) {
      toast?.({ tone: 'danger', message: "Couldn't save changes — try again" });
      setSaving(false);
    }
  };

  const locFields = (label, form, set, dt, setDt) => (
    <div className="space-y-3">
      <div className="text-11 font-semibold uppercase tracking-wider text-body/45">{label}</div>
      {dt !== null && (
        <Field label="Pickup date & time">
          <TextInput type="datetime-local" value={dt} min={nowLocalISO()} onChange={(e) => setDt(e.target.value)} />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Address"><TextInput value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} placeholder="Street" /></Field>
        <Field label="City"><TextInput value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label="State"><TextInput value={form.state} onChange={(e) => set('state', e.target.value)} /></Field>
        <Field label="Postal code"><TextInput value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Airport code" hint="e.g. ORD — leave blank if not an airport"><TextInput value={form.airportCode} onChange={(e) => set('airportCode', e.target.value)} /></Field>
        <Field label="Airline"><TextInput value={form.airline} onChange={(e) => set('airline', e.target.value)} /></Field>
        <Field label="Flight number"><TextInput value={form.flightNumber} onChange={(e) => set('flightNumber', e.target.value)} /></Field>
        <Field label="Terminal"><TextInput value={form.terminal} onChange={(e) => set('terminal', e.target.value)} /></Field>
        <Field label="Flight time"><TextInput type="datetime-local" value={form.flightTime} onChange={(e) => set('flightTime', e.target.value)} /></Field>
      </div>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit trip — ${customerLabel}`}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" size="md" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {row.bookingNumber && (
          <div className="-mt-1 font-mono text-[12px] font-semibold text-body/60">Booking {row.bookingNumber}</div>
        )}
        <div className="space-y-3">
          <div className="text-11 font-semibold uppercase tracking-wider text-body/45">Customer</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name"><TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
            <Field label="Last name"><TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
          </div>
        </div>
        <div className="border-t border-border" />
        {locFields('Pickup', pickup, setP, pickupDt, setPickupDt)}
        <div className="border-t border-border" />
        {locFields('Drop-off', dropoff, setD, null, null)}
        <div className="border-t border-border" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Travelers"><TextInput type="number" min="1" value={numTravelers} onChange={(e) => setNumTravelers(e.target.value)} /></Field>
          <Field label="Bags"><TextInput type="number" min="0" value={numBags} onChange={(e) => setNumBags(e.target.value)} /></Field>
        </div>
        <div className="border-t border-border" />
        <div className="text-11 font-semibold uppercase tracking-wider text-body/45">Dispatch</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Driver" hint="Changing the driver sends them a fresh request to accept.">
            <Select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Unassigned</option>
              {drivers.map((d) => (
                <option key={d.driverId} value={String(d.driverId)}>{d.firstName} {d.lastName}</option>
              ))}
            </Select>
          </Field>
          <Field label="Vehicle">
            <Select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Unassigned</option>
              {vehicles.map((v) => (
                <option key={v.vehicleId} value={String(v.vehicleId)}>
                  {v.vehicleName || v.plateNumber || `Vehicle #${v.vehicleId}`}{v.capacity ? ` · ${v.capacity} seats` : ''}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Status" hint="Setting En route / Completed drives the driver app and lifecycle emails.">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {TRIP_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
            ))}
          </Select>
        </Field>
        <Field label="Notes"><TextInput value={comments} onChange={(e) => setComments(e.target.value)} placeholder="Notes for the driver / dispatcher" /></Field>
        {testToolsEnabled && (
          <label className="flex items-center gap-2 text-[12px] text-body/70">
            <input type="checkbox" checked={suppress} onChange={(e) => setSuppress(e.target.checked)} />
            Suppress notifications (test setup) — no customer/driver emails for this save
          </label>
        )}
      </div>
    </Modal>
  );
}
